import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError, setNativeSession } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState("");

  const refresh = useCallback(async () => {
    // Background balance refreshes must keep protected pages mounted.
    setConnectionError("");
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch (error) {
      if (error.response?.status === 401) {
        try { await setNativeSession(""); }
        catch { setConnectionError("Unable to clear the expired sign-in. Please retry."); return null; }
        setUser(false);
      } else {
        setConnectionError("Unable to reconnect to your account. Check that your backend and connection are available, then retry.");
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    await setNativeSession(data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password, referralCode, verificationToken) => {
    const { data } = await api.post("/auth/register", {
      name, email, password,
      referral_code: referralCode || undefined,
      verification_token: verificationToken,
    });
    await setNativeSession(data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await setNativeSession(""); }
    catch {
      setConnectionError("Unable to clear the saved sign-in securely. Unlock your device and retry.");
      return;
    }
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, connectionError, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { formatApiError };
